import { getAuth } from 'firebase/auth';
import { deleteField, getDocs, serverTimestamp, Timestamp, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { normalizeCombos, userProjectDocRef, userVideoDocRef, userVideosCollRef, userProjectsCollRef } from '../utils';
import { VIDEO_LEARNING_STATUS_VALUES, normalizeVideoSettingsViewCrop } from '../types';
import { deleteProject, renameProject, updateProjectSegments } from './projectService';
import { FileUploadService } from './fileUploadService';
import { getDefaultYouTubeThumbnailUrl } from './youtubeMetadataService';
import {
  deleteGuestLibraryRecord,
  getGuestLibraryVideoUrl,
  isGuestLibraryProject,
  listGuestLibraryProjects,
  updateGuestLibraryProject
} from './guestLibraryService';
import type {
  LegacyVideoProjectSource,
  LibraryItem,
  LibraryItemCollection,
  LibraryProjectMediaAvailability,
  LocalDeviceProjectSource,
  NormalizedLibraryProject,
  NormalizedLibraryProjectSource,
  PracticeProject,
  SharedSession,
  Segment,
  UploadProjectSource,
  VideoLearningStatus,
  VideoMeta,
  VideoSettings,
  YouTubeProjectSource
} from '../types';

export { isGuestLibraryProject } from './guestLibraryService';

const getErrorMessage = (error: unknown): string => {
  return error instanceof Error ? error.message : String(error);
};

const getCurrentUid = (): string => {
  const { currentUser } = getAuth();
  if (!currentUser) throw new Error('Not authenticated');
  return currentUser.uid;
};

export const normalizeVideoLearningStatus = (value: unknown): VideoLearningStatus | undefined => {
  return VIDEO_LEARNING_STATUS_VALUES.includes(value as VideoLearningStatus)
    ? value as VideoLearningStatus
    : undefined;
};

export const normalizePlaylistId = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const optionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  return value.trim().length > 0 ? value : undefined;
};

const optionalNumber = (value: unknown): number | undefined => {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const normalizeUploadProjectSource = (value: unknown): UploadProjectSource => {
  const source = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const normalizedSource: UploadProjectSource = {
    type: 'upload',
    storagePath: optionalString(source.storagePath) || '',
    uploadSize: optionalNumber(source.uploadSize) || 0
  };

  const duration = optionalNumber(source.duration);
  const thumbnailPath = optionalString(source.thumbnailPath);
  if (duration !== undefined) normalizedSource.duration = duration;
  if (thumbnailPath) normalizedSource.thumbnailPath = thumbnailPath;

  return normalizedSource;
};

const normalizeYouTubeProjectSource = (value: unknown): YouTubeProjectSource => {
  const source = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const normalizedSource: YouTubeProjectSource = {
    type: 'youtube',
    videoId: optionalString(source.videoId) || '',
    canonicalUrl: optionalString(source.canonicalUrl) || ''
  };

  const startTime = optionalNumber(source.startTime);
  const videoTitle = optionalString(source.videoTitle);
  const thumbnailUrl = optionalString(source.thumbnailUrl);
  if (startTime !== undefined) normalizedSource.startTime = startTime;
  if (videoTitle) normalizedSource.videoTitle = videoTitle;
  if (thumbnailUrl) normalizedSource.thumbnailUrl = thumbnailUrl;

  return normalizedSource;
};

const normalizeLocalDeviceProjectSource = (value: unknown): LocalDeviceProjectSource => {
  const source = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const normalizedSource: LocalDeviceProjectSource = {
    type: 'local-device',
    localUri: optionalString(source.localUri) || '',
    size: optionalNumber(source.size) || 0
  };

  const fileFingerprint = optionalString(source.fileFingerprint);
  const duration = optionalNumber(source.duration);
  const thumbnailUri = optionalString(source.thumbnailUri);
  if (fileFingerprint) normalizedSource.fileFingerprint = fileFingerprint;
  if (duration !== undefined) normalizedSource.duration = duration;
  if (thumbnailUri) normalizedSource.thumbnailUri = thumbnailUri;

  return normalizedSource;
};

const normalizeLibraryProjectSource = (value: unknown): NormalizedLibraryProjectSource => {
  const source = value && typeof value === 'object' ? value as Record<string, unknown> : {};

  if (source.type === 'youtube') return normalizeYouTubeProjectSource(source);
  if (source.type === 'local-device') return normalizeLocalDeviceProjectSource(source);
  return normalizeUploadProjectSource(source);
};

const normalizeLegacyVideoSource = (data: Record<string, unknown>): LegacyVideoProjectSource => {
  const normalizedSource: LegacyVideoProjectSource = {
    type: 'legacy-video',
    storagePath: optionalString(data.storagePath) || '',
    size: optionalNumber(data.size) || 0
  };

  const duration = optionalNumber(data.duration);
  const thumbnailPath = optionalString(data.thumbnailPath);
  if (duration !== undefined) normalizedSource.duration = duration;
  if (thumbnailPath) normalizedSource.thumbnailPath = thumbnailPath;

  return normalizedSource;
};

const sharedFieldsFrom = (data: Record<string, unknown>) => ({
  currentShareId: data.currentShareId as string | null | undefined,
  currentShareUrl: data.currentShareUrl as string | null | undefined,
  currentShareExpiresAt: data.currentShareExpiresAt as any,
  currentShareViewCount: data.currentShareViewCount as number | undefined,
  currentShareMaxViews: data.currentShareMaxViews as number | undefined,
  currentShareIsActive: data.currentShareIsActive as boolean | undefined,
  currentShareCreatedAt: data.currentShareCreatedAt as any,
  totalSharesCreated: data.totalSharesCreated as number | undefined,
  totalShareViews: data.totalShareViews as number | undefined
});

const normalizeLegacyVideoDoc = (d: any): NormalizedLibraryProject => {
  const data = d.data() as any;
  return {
    id: d.id,
    source: normalizeLegacyVideoSource(data),
    title: data.title,
    segments: normalizeCombos(data.segments || []),
    settings: normalizeVideoSettingsViewCrop(data.settings || {}) as VideoSettings,
    learningStatus: normalizeVideoLearningStatus(data.learningStatus),
    playlistId: normalizePlaylistId(data.playlistId),
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    ...sharedFieldsFrom(data)
  };
};

const normalizeProjectDoc = (d: any): NormalizedLibraryProject => {
  const data = d.data() as any;
  const source = normalizeLibraryProjectSource(data.source);

  return {
    id: d.id,
    source,
    title: data.title,
    segments: normalizeCombos(data.segments || []),
    settings: normalizeVideoSettingsViewCrop(data.settings || {}) as VideoSettings,
    learningStatus: normalizeVideoLearningStatus(data.learningStatus),
    playlistId: normalizePlaylistId(data.playlistId),
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    ...sharedFieldsFrom(data)
  };
};

export const normalizedLibraryProjectToLibraryItem = (
  project: NormalizedLibraryProject
): LibraryItem => {
  const common = {
    id: project.id,
    title: project.title,
    segments: project.segments,
    settings: project.settings,
    learningStatus: project.learningStatus,
    playlistId: project.playlistId,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    ...sharedFieldsFrom(project as unknown as Record<string, unknown>)
  };

  if (project.source.type === 'youtube') {
    return {
      ...common,
      _collection: 'projects' as const,
      source: project.source,
      storagePath: '',
      size: 0
    };
  }

  if (project.source.type === 'legacy-video') {
    return {
      ...common,
      _collection: 'videos' as const,
      storagePath: project.source.storagePath,
      thumbnailPath: project.source.thumbnailPath,
      size: project.source.size
    };
  }

  if (project.source.type === 'local-device') {
    return {
      ...common,
      _collection: 'projects' as const,
      source: project.source,
      storagePath: '',
      size: project.source.size
    };
  }

  return {
    ...common,
    _collection: 'projects' as const,
    source: project.source,
    storagePath: project.source.storagePath,
    thumbnailPath: project.source.thumbnailPath,
    size: project.source.uploadSize
  };
};

export async function listUserVideos(): Promise<VideoMeta[]> {
  const { currentUser } = getAuth();
  if (!currentUser) return [];
  const uid = currentUser.uid;

  const snap = await getDocs(userVideosCollRef(uid));
  return snap.docs.map(d => {
    const data = d.data() as any;
    return {
      id: d.id,
      ...data,
      segments: normalizeCombos(data.segments || []),
      settings: normalizeVideoSettingsViewCrop(data.settings || {}) as VideoSettings,
      learningStatus: normalizeVideoLearningStatus(data.learningStatus),
      playlistId: normalizePlaylistId(data.playlistId)
    };
  });
}

/**
 * List all practice projects from both current projects and legacy videos.
 * Collection names and legacy document shape stay inside this persistence
 * module; callers receive one normalized project contract.
 */
export async function listLibraryProjects(): Promise<NormalizedLibraryProject[]> {
  const { currentUser } = getAuth();
  let localProjects: NormalizedLibraryProject[] = [];
  if (typeof window !== 'undefined' && window.indexedDB) {
    try {
      localProjects = await listGuestLibraryProjects();
    } catch (error) {
      if (!currentUser) throw error;
      console.warn('Could not read device-only library items; cloud items will still be shown.', error);
    }
  }
  if (!currentUser) return localProjects;
  const uid = currentUser.uid;

  const [videosResult, projectsResult] = await Promise.allSettled([
    getDocs(userVideosCollRef(uid)),
    getDocs(userProjectsCollRef(uid))
  ]);

  if (videosResult.status === 'rejected') {
    console.warn('Failed to read legacy videos collection:', videosResult.reason);
  }

  if (projectsResult.status === 'rejected') {
    console.warn('Failed to read projects collection:', projectsResult.reason);
  }

  if (videosResult.status === 'rejected' && projectsResult.status === 'rejected') {
    throw new Error(
      'Could not read your library. Firebase denied access to both library collections. ' +
      'Deploy Firestore rules that allow the signed-in owner to read users/{uid}/projects and users/{uid}/videos. ' +
      `Details: projects: ${getErrorMessage(projectsResult.reason)}; videos: ${getErrorMessage(videosResult.reason)}`
    );
  }

  const legacyItems: NormalizedLibraryProject[] =
    videosResult.status === 'fulfilled' ? videosResult.value.docs.map(normalizeLegacyVideoDoc) : [];

  const projectItems: NormalizedLibraryProject[] =
    projectsResult.status === 'fulfilled' ? projectsResult.value.docs.map(normalizeProjectDoc) : [];

  // Projects first (newest saves), then legacy items
  return [...localProjects, ...projectItems, ...legacyItems];
}

/**
 * Compatibility adapter for legacy callers and characterization tests that
 * still exercise the previous LibraryItem shape.
 */
export async function listAllLibraryItems(): Promise<LibraryItem[]> {
  const projects = await listLibraryProjects();
  return projects.map(normalizedLibraryProjectToLibraryItem);
}

export async function getLibraryProjectVideoUrl(
  project: NormalizedLibraryProject
): Promise<string> {
  if (project.source.type === 'youtube') {
    return '';
  }

  if (project.source.type === 'local-device') {
    if (isGuestLibraryProject(project)) {
      return getGuestLibraryVideoUrl(project.id);
    }
    throw new Error('This video is only available on this device and cannot be opened in the web app.');
  }

  const path = project.source.storagePath;
  if (!path) {
    throw new Error('This library project is missing its uploaded video path.');
  }

  return getDownloadURL(storageRef(storage, path));
}

export const getLibraryProjectCollection = (
  project: NormalizedLibraryProject
): LibraryItemCollection => (
  project.source.type === 'legacy-video' ? 'videos' : 'projects'
);

export const getLibraryProjectMembershipKey = (
  project: NormalizedLibraryProject
): string => `${getLibraryProjectCollection(project)}:${project.id}`;

export const libraryProjectMatchesMembership = (
  project: NormalizedLibraryProject,
  membership: { itemId: string; itemCollection: LibraryItemCollection }
): boolean => (
  membership.itemId === project.id &&
  membership.itemCollection === getLibraryProjectCollection(project)
);

export const libraryProjectToPlaylistReference = (
  project: NormalizedLibraryProject
): Pick<LibraryItem, 'id' | '_collection'> => ({
  id: project.id,
  _collection: getLibraryProjectCollection(project)
});

export const isYouTubeLibraryProject = (
  project: NormalizedLibraryProject
): project is NormalizedLibraryProject & { source: YouTubeProjectSource } => (
  project.source.type === 'youtube'
);

export const isLocalDeviceLibraryProject = (
  project: NormalizedLibraryProject
): project is NormalizedLibraryProject & { source: LocalDeviceProjectSource } => (
  project.source.type === 'local-device'
);

export const getLibraryProjectStoragePath = (
  project: NormalizedLibraryProject
): string => {
  if (project.source.type === 'upload' || project.source.type === 'legacy-video') {
    return project.source.storagePath;
  }
  return '';
};

export const getLibraryProjectThumbnailPath = (
  project: NormalizedLibraryProject
): string | undefined => {
  if (project.source.type === 'upload' || project.source.type === 'legacy-video') {
    return project.source.thumbnailPath;
  }
  return undefined;
};

export const getLibraryProjectYouTubeThumbnailUrl = (
  project: NormalizedLibraryProject
): string | null => {
  if (!isYouTubeLibraryProject(project)) return null;
  return project.source.thumbnailUrl || getDefaultYouTubeThumbnailUrl(project.source.videoId);
};

export const getLibraryProjectSize = (
  project: NormalizedLibraryProject
): number => {
  if (project.source.type === 'upload') return project.source.uploadSize;
  if (project.source.type === 'legacy-video') return project.source.size;
  if (project.source.type === 'local-device') return project.source.size;
  return 0;
};

export const libraryProjectConsumesUploadSlot = (
  project: NormalizedLibraryProject
): boolean => (
  project.source.type === 'upload' || project.source.type === 'legacy-video'
);

export const getLibraryProjectMediaAvailability = (
  project: NormalizedLibraryProject
): LibraryProjectMediaAvailability => {
  if (isGuestLibraryProject(project)) {
    return {
      status: 'device-local',
      label: 'Saved on this device',
      isPlayableInCurrentApp: true,
      isCrossDevicePlayable: false,
      requiresFirebaseMediaUrl: false
    };
  }

  if (project.source.type === 'youtube') {
    return {
      status: 'youtube',
      label: 'No upload storage',
      isPlayableInCurrentApp: true,
      isCrossDevicePlayable: true,
      requiresFirebaseMediaUrl: false
    };
  }

  if (project.source.type === 'local-device') {
    return {
      status: 'device-local',
      label: 'This device only',
      isPlayableInCurrentApp: false,
      isCrossDevicePlayable: false,
      requiresFirebaseMediaUrl: false
    };
  }

  if (!project.source.storagePath) {
    return {
      status: 'missing-cloud-media',
      label: 'Missing cloud video',
      isPlayableInCurrentApp: false,
      isCrossDevicePlayable: false,
      requiresFirebaseMediaUrl: true
    };
  }

  return {
    status: 'cloud',
    label: 'Cloud video',
    isPlayableInCurrentApp: true,
    isCrossDevicePlayable: true,
    requiresFirebaseMediaUrl: true
  };
};

export const normalizeSavedPracticeProject = (
  id: string,
  project: Omit<PracticeProject, 'id'>
): NormalizedLibraryProject => {
  const rawProject = project as unknown as Record<string, unknown>;
  const source = normalizeLibraryProjectSource(project.source);

  return {
    id,
    source,
    title: String(project.title || ''),
    segments: normalizeCombos(project.segments || []),
    settings: normalizeVideoSettingsViewCrop(project.settings || {}) as VideoSettings,
    learningStatus: normalizeVideoLearningStatus(rawProject.learningStatus),
    playlistId: normalizePlaylistId(rawProject.playlistId),
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    ...sharedFieldsFrom(rawProject)
  };
};

const libraryProjectDocRef = (project: NormalizedLibraryProject) => {
  const uid = getCurrentUid();
  return getLibraryProjectCollection(project) === 'projects'
    ? userProjectDocRef(uid, project.id)
    : userVideoDocRef(uid, project.id);
};

export async function deleteLibraryProject(project: NormalizedLibraryProject): Promise<void> {
  if (isGuestLibraryProject(project)) {
    await deleteGuestLibraryRecord(project.id);
    return;
  }

  if (project.source.type === 'legacy-video') {
    await FileUploadService.deleteFromLibrary(project.id, project.source.storagePath);
    return;
  }

  await deleteProject(project.id);
}

export async function updateLibraryProjectSegments(
  project: NormalizedLibraryProject,
  segments: Segment[],
  settings: VideoSettings
): Promise<void> {
  if (isGuestLibraryProject(project)) {
    await updateGuestLibraryProject(project.id, { segments, settings });
    return;
  }

  if (project.source.type === 'legacy-video') {
    await FileUploadService.updateLibrarySegments(project.id, segments, settings);
    return;
  }

  await updateProjectSegments(project.id, segments, settings);
}

export async function renameLibraryProject(
  project: NormalizedLibraryProject,
  title: string
): Promise<void> {
  if (isGuestLibraryProject(project)) {
    await updateGuestLibraryProject(project.id, { title });
    return;
  }

  if (project.source.type !== 'legacy-video') {
    await renameProject(project.id, title);
    return;
  }

  const uid = getCurrentUid();
  await updateDoc(userVideoDocRef(uid, project.id), {
    title,
    updatedAt: serverTimestamp()
  });
}

export async function updateLibraryProjectLearningStatus(
  project: NormalizedLibraryProject,
  learningStatus: VideoLearningStatus | undefined
): Promise<void> {
  if (isGuestLibraryProject(project)) {
    await updateGuestLibraryProject(project.id, { learningStatus });
    return;
  }
  await updateDoc(libraryProjectDocRef(project), {
    learningStatus: learningStatus || deleteField(),
    updatedAt: serverTimestamp()
  });
}

export async function persistLibraryProjectLoadMigrations(
  project: NormalizedLibraryProject,
  updates: Record<string, unknown>
): Promise<void> {
  if (Object.keys(updates).length === 0) return;

  if (isGuestLibraryProject(project)) {
    const dottedSettings = Object.entries(updates).filter(([key]) => key.startsWith('settings.'));
    const settings = updates.settings && typeof updates.settings === 'object'
      ? updates.settings as VideoSettings
      : dottedSettings.length > 0
        ? dottedSettings.reduce<Record<string, unknown>>((next, [key, value]) => {
            next[key.slice('settings.'.length)] = value;
            return next;
          }, { ...project.settings }) as unknown as VideoSettings
        : undefined;
    await updateGuestLibraryProject(project.id, {
      segments: Array.isArray(updates.segments) ? updates.segments as Segment[] : undefined,
      settings
    });
    return;
  }

  await updateDoc(libraryProjectDocRef(project), {
    ...updates,
    updatedAt: serverTimestamp()
  });
}

export async function markLibraryProjectShared(
  project: NormalizedLibraryProject,
  share: Pick<SharedSession, 'shareId'> & { shareUrl: string; expiresAt: Date }
): Promise<void> {
  await updateDoc(libraryProjectDocRef(project), {
    currentShareId: share.shareId,
    currentShareUrl: share.shareUrl,
    currentShareExpiresAt: Timestamp.fromDate(share.expiresAt),
    updatedAt: serverTimestamp()
  });
}

export async function clearLibraryProjectShare(
  project: NormalizedLibraryProject
): Promise<void> {
  await updateDoc(libraryProjectDocRef(project), {
    currentShareId: null,
    currentShareUrl: null,
    currentShareExpiresAt: null,
    updatedAt: serverTimestamp()
  });
}
